import { FormattedMessage } from 'react-intl';

// Human wording for the `ai_generated_source` strings the server records:
//   declared                 — the author ticked the compose toggle
//   c2pa:<tool> / iptc:<x>   — provenance metadata in the original file
//   exif:<tool> / xmp:<tool> — generator named in EXIF / XMP creator fields
//   png:<generator>          — Stable Diffusion front-end sidecar chunk
//   sightengine:<percent>    — hosted image classifier confidence
//   pangram                  — hosted text detector verdict
export const describeAiSource = (source?: string | null): React.ReactNode => {
  const [kind, detail] = (source ?? '').split(':', 2) as [
    string,
    string | undefined,
  ];

  switch (kind) {
    case 'declared':
      return (
        <FormattedMessage
          id='ai_source.declared'
          defaultMessage='Marked as AI-generated by the author.'
        />
      );
    case 'c2pa':
      return (
        <FormattedMessage
          id='ai_source.c2pa'
          defaultMessage='Content Credentials (C2PA) in the original file record it as AI-generated{tool, select, none {} other { by {tool}}}.'
          values={{ tool: detail ?? 'none' }}
        />
      );
    case 'iptc':
      return (
        <FormattedMessage
          id='ai_source.iptc'
          defaultMessage='The original file’s IPTC metadata declares it as synthetic media{tool, select, none {} other { ({tool})}}.'
          values={{ tool: detail ?? 'none' }}
        />
      );
    case 'exif':
    case 'xmp':
      return (
        <FormattedMessage
          id='ai_source.creator_tool'
          defaultMessage='The original file names an AI generator as its creator{tool, select, none {} other {: {tool}}}.'
          values={{ tool: detail ?? 'none' }}
        />
      );
    case 'png':
      return (
        <FormattedMessage
          id='ai_source.png'
          defaultMessage='The original file carries generation parameters from {tool}.'
          values={{ tool: detail ?? 'a local image generator' }}
        />
      );
    case 'sightengine':
      return (
        <FormattedMessage
          id='ai_source.classifier'
          defaultMessage='An image classifier rated it {percent}% likely to be AI-generated. This is a statistical guess, not proof.'
          values={{ percent: detail ?? '?' }}
        />
      );
    case 'pangram':
      return (
        <FormattedMessage
          id='ai_source.pangram'
          defaultMessage='A text detector rated this post as AI-written. Detectors are not proof and can be wrong.'
        />
      );
    default:
      return (
        <FormattedMessage
          id='ai_source.unknown'
          defaultMessage='Labeled as AI-generated.'
        />
      );
  }
};
